package org.zest.insan_360_website.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.functions.max
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.Input
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.common.PlaceholderColor
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Text
import org.zest.insan_360_website.components.layouts.*
import org.zest.insan_360_website.components.widgets.Loading
import org.zest.insan_360_website.components.widgets.LocalToast
import org.zest.insan_360_website.components.widgets.ToastType
import org.zest.insan_360_website.data.localized
import org.zest.insan_360_website.data.page_states.AuthState.requestLogin
import org.zest.insan_360_website.data.page_states.LoginPageState.loginState

val toLeft32Px = Keyframes {
    0.percent {
        Modifier.translate(0.px, 0.px)
    }
    15.percent {
        Modifier.translate((-32).px, 0.px)
    }
    30.percent {
        Modifier.translate((-32).px, 0.px)
    }
    45.percent {
        Modifier.translate(0.px, 0.px)
    }
}

val toRight32Px = Keyframes {
    0.percent {
        Modifier.translate(0.px, 0.px)
    }
    15.percent {
        Modifier.translate(32.px, 0.px)
    }
    30.percent {
        Modifier.translate(32.px, 0.px)
    }
    45.percent {
        Modifier.translate(0.px, 0.px)
    }
}

val containerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Flex)
            .flexDirection(FlexDirection.Column)
    }

    Breakpoint.LG {
        Modifier
            .fillMaxWidth(65.percent)
            .flexDirection(FlexDirection.Row)
            .maxWidth(1280.px)
    }
}

data class LoginState(
    val email: String = "",
    val password: String = "",
    val isLoading: Boolean = false
) {
    companion object {
        private val EMAIL_REGEX = Regex("^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\\.[a-zA-Z]+$")
    }

    fun validate(): String? {
        val sanitizedEmail = email.trim()
        if (sanitizedEmail.isBlank() || password.isBlank()) {
            return authWordLibrary.enterEmailPassword.localized
        }
        if (!sanitizedEmail.matches(EMAIL_REGEX)) {
            return authWordLibrary.enterValidEmail.localized
        }
        return null
    }
}

@Page("index")
@Layout(".components.layouts.MainPageLayout")
@Composable
fun LoginPage(ctx: PageContext) {
    val scope = rememberCoroutineScope()
    val toast = LocalToast.current

    LoginPageContentContainer {
        Column(
            Modifier
                .backgroundColor(darkGreenColor)
                .padding(2.cssRem)
                .flex(3)
                .gap(2.cssRem)
                .color(Colors.White)
        ) {
            SpanText(
                modifier = Modifier
                    .fontWeight(FontWeight.Bold)
                    .fontSize(1.cssRem)
                    .fillMaxWidth()
                    .textAlignLast(TextAlignLast.Justify),
                text = authWordLibrary.mainTitle.localized
            )
            Column(
                Modifier
                    .fillMaxWidth()
                    .fontWeight(FontWeight.Bolder)
                    .fontSize(2.cssRem),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Row {
                    SpanText(
                        modifier = Modifier
                            .lineHeight(2.cssRem)
                            .animation(
                                toLeft32Px.toAnimation(
                                    duration = 6.s,
                                    iterationCount = AnimationIterationCount.Infinite,
                                    timingFunction = AnimationTimingFunction.EaseInOut,
                                    delay = 150.ms
                                )
                            ),
                        text = "Your "
                    )
                    SpanText(
                        modifier = Modifier
                            .lineHeight(2.cssRem)
                            .animation(
                                toLeft32Px.toAnimation(
                                    duration = 6.s,
                                    iterationCount = AnimationIterationCount.Infinite,
                                    timingFunction = AnimationTimingFunction.EaseInOut,
                                    delay = 250.ms
                                )
                            ),
                        text = "Gateway "
                    )
                    SpanText(
                        modifier = Modifier
                            .lineHeight(2.cssRem)
                            .animation(
                                toLeft32Px.toAnimation(
                                    duration = 6.s,
                                    iterationCount = AnimationIterationCount.Infinite,
                                    timingFunction = AnimationTimingFunction.EaseInOut,
                                    delay = 350.ms
                                )
                            ),
                        text = "to"
                    )
                }
                SpanText(
                    modifier = Modifier
                        .lineHeight(2.cssRem),
                    text = "Personal & Team"
                )
                SpanText(
                    modifier = Modifier
                        .lineHeight(2.cssRem)
                        .animation(
                            toRight32Px.toAnimation(
                                duration = 6.s,
                                iterationCount = AnimationIterationCount.Infinite,
                                timingFunction = AnimationTimingFunction.EaseInOut,
                                delay = 450.ms
                            )
                        ),
                    text = "Transformation"
                )
            }
            Row(
                Modifier
                    .fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(
                    modifier = Modifier
                        .flex(5)
                        .textAlign(TextAlign.Center)
                        .fontSize(0.85.cssRem)
                        .fontWeight(FontWeight.SemiBold),
                    text = authWordLibrary.mainBullet_1.localized,
                )
                Box(
                    Modifier.flex(1),
                    contentAlignment = Alignment.Center
                ) {
                    Box(
                        Modifier
                            .size(5.px)
                            .backgroundColor(Colors.White)
                            .borderRadius(99.px)
                    )
                }
                SpanText(
                    modifier = Modifier
                        .flex(5)
                        .textAlign(TextAlign.Center)
                        .fontSize(0.85.cssRem)
                        .fontWeight(FontWeight.SemiBold),
                    text = authWordLibrary.mainBullet_2.localized,
                )
                Box(
                    Modifier.flex(1),
                    contentAlignment = Alignment.Center
                ) {
                    Box(
                        Modifier
                            .size(5.px)
                            .backgroundColor(Colors.White)
                            .borderRadius(99.px)
                    )
                }
                SpanText(
                    modifier = Modifier
                        .flex(5)
                        .textAlign(TextAlign.Center)
                        .fontSize(0.85.cssRem)
                        .fontWeight(FontWeight.SemiBold),
                    text = authWordLibrary.mainBullet_3.localized,
                )
            }
            SpanText(
                modifier = Modifier
                    .fillMaxWidth()
                    .fontSize(0.75.cssRem)
                    .textAlign(TextAlign.Center),
                text = authWordLibrary.mainPara.localized
            )
            Column(
                Modifier
                    .gap(0.25.cssRem)
            ) {
                SpanText(
                    modifier = Modifier
                        .fontWeight(FontWeight.SemiBold),
                    text = authWordLibrary.mainList_1.localized
                )
                SpanText(
                    modifier = Modifier
                        .fontWeight(FontWeight.Normal)
                        .fontSize(max(0.75.cssRem, 12.px)),
                    text = authWordLibrary.mainListSub_1.localized
                )
            }
            Column(
                Modifier
                    .gap(0.25.cssRem)
            ) {
                SpanText(
                    modifier = Modifier
                        .fontWeight(FontWeight.SemiBold),
                    text = authWordLibrary.mainList_2.localized
                )
                SpanText(
                    modifier = Modifier
                        .fontWeight(FontWeight.Normal)
                        .fontSize(max(0.75.cssRem, 12.px)),
                    text = authWordLibrary.mainListSub_2.localized
                )
            }
            Column(
                Modifier
                    .gap(0.25.cssRem)
            ) {
                SpanText(
                    modifier = Modifier
                        .fontWeight(FontWeight.SemiBold),
                    text = authWordLibrary.mainList_3.localized
                )
                SpanText(
                    modifier = Modifier
                        .fontWeight(FontWeight.Normal)
                        .fontSize(max(0.75.cssRem, 12.px)),
                    text = authWordLibrary.mainListSub_3.localized
                )
            }
            SpanText(
                modifier = Modifier
                    .fillMaxWidth()
                    .fontSize(max(0.75.cssRem, 12.px))
                    .textAlign(TextAlign.Center),
                text = authWordLibrary.mainLastSub.localized
            )
        }
        Column(
            Modifier
                .flex(4)
        ) {
            Box(
                Modifier
                    .fillMaxWidth()
                    .padding(topBottom = 1.cssRem)
                    .backgroundColor(lightGreenColor)
                    .border {
                        style(LineStyle.Solid)
                        width(top = 0.px, bottom = 2.px, left = 0.px, right = 0.px)
                        color(darkGreenColor)
                    },
                contentAlignment = Alignment.Center
            ) {
                Image(
                    modifier = Modifier
                        .height(9.cssRem),
                    src = "/images/insan360_logo.webp",
                    description = "Insan 360",
                )
            }
            LoginInputLayout(
                loginState,
                { mutator -> loginState = mutator(loginState) }
            ) {
                loginState = loginState.copy(isLoading = true)
                val errorMessage = loginState.validate()
                if (errorMessage != null) {
                    toast.show(errorMessage, ToastType.ERROR)
                    loginState = loginState.copy(isLoading = false)
                } else requestLogin(loginState.email, loginState.password, toast, ctx, scope) {
                    loginState = loginState.copy(isLoading = false)
                }
            }
        }
    }
}

@Composable
fun LoginPageContentContainer(content: @Composable () -> Unit) {
    Box(
        Modifier.fillMaxSize().then(fontSizeBreakpoints.toModifier()),
        contentAlignment = Alignment.Center
    ) {
        Div(
            containerStyle.toModifier()
                .position(Position.Relative)
                .borderRadius(2.cssRem)
                .border {
                    width(2.px)
                    color(darkGreenColor)
                    style(LineStyle.Solid)
                }
                .overflow(Overflow.Hidden)
                .toAttrs(),
        ) {
            content()
        }
    }
}

@Composable
fun LoginInputLayout(
    state: LoginState,
    onStateChange: ((LoginState) -> LoginState) -> Unit,
    onSubmitAction: () -> Unit
) {
    Form(
        attrs = Modifier.fillMaxSize().toAttrs {
            onSubmit {
                it.preventDefault()
                onSubmitAction()
            }
        }
    ) {
        Column(
            Modifier
                .backgroundColor(Colors.White)
                .fillMaxSize()
                .zIndex(1)
                .padding(2.cssRem)
                .gap(1.cssRem),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            H2(
                Modifier
                    .textAlign(TextAlign.Center)
                    .margin(0.px)
                    .toAttrs()
            )
            { Text(authWordLibrary.welcome.localized) }
            Input(
                modifier = baseInputStyle.toModifier().attrsModifier {
                    attr("autocomplete", "username")
                    attr("name", "username")
                },
                focusBorderColor = lightGreenColor,
                placeholderColor = PlaceholderColor(lightGreenColor),
                type = InputType.Email,
                value = state.email,
                onValueChange = { newValue -> onStateChange { it.copy(email = newValue) } },
                placeholder = authWordLibrary.email.localized
            )
            Input(
                modifier = baseInputStyle.toModifier().attrsModifier {
                    attr("autocomplete", "current-password")
                    attr("name", "current-password")
                },
                focusBorderColor = lightGreenColor,
                placeholderColor = PlaceholderColor(lightGreenColor),
                type = InputType.Password,
                value = state.password,
                onValueChange = { newValue -> onStateChange { it.copy(password = newValue) } },
                placeholder = authWordLibrary.password.localized
            )
            Button(
                enabled = !(state.isLoading),
                onClick = { onSubmitAction() },
                modifier = buttonColorStyle.toModifier()
                    .fillMaxWidth()
            ) {
                if (state.isLoading) {
                    Loading()
                } else {
                    SpanText(
                        authWordLibrary.login.localized,
                        contentButtonTextStyle.toModifier().fontSize(1.cssRem)
                    )
                }
            }
            A(
                attrs = Modifier
                    .fontSize(0.75.cssRem)
                    .color(darkGreenColor)
                    .toAttrs(),
                href = "/register"
            ) {
                Text(
                    authWordLibrary.noAccount.localized
                )
            }
            A(
                attrs = Modifier
                    .fontSize(0.75.cssRem)
                    .color(darkGreenColor)
                    .toAttrs(),
                href = "/forgot-password"
            ) {
                Text(
                    authWordLibrary.forgotPassword.localized
                )
            }
        }
    }
}